import {
  ActionIcon,
  Button,
  Checkbox,
  Divider,
  Group,
  Menu,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconCheck,
  IconChevronDown,
  IconMail,
  IconMailOpened,
  IconRefresh,
  IconSortAscending,
  IconSortDescending,
  IconTrash,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import type { Table } from '@tanstack/react-table';
import { ExternalMailActionAccountManager } from 'app/Private/ExternalMailbox/ExternalMailActions/ExternalMailActionAccountManager';
import { ExternalMailActionDebtor } from 'app/Private/ExternalMailbox/ExternalMailActions/ExternalMailActionDebtor';
import {
  ExternalMailActionFormProvider,
  useExternalMailActionForm,
} from 'app/Private/ExternalMailbox/ExternalMailActions/form-context';
import { buildBatchActionProperties } from 'app/Private/Invoices/Listing/batch-action-analytics';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, tKeys, useTranslation } from 'locales';

import { MailCategoryFilterBar } from 'modules/Mails/components/MailCategoryFilterBar';
import type { LightThread } from 'modules/Mails/hooks/use-load-mails';

import type { MailFiltersForm } from 'modules/Mails/hooks/use-mail-filters';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { captureEvent } from 'shared/analytics/capture-event';
import {
  ActionTypes,
  useBatchUpdateExternalMails,
} from 'shared/hooks/use-batch-update-external-mails';
import { useManuallySyncExternalMails } from 'shared/hooks/use-manually-sync-external-mails';

const SORT_FIELDS = [
  { value: 'date', labelKey: i18nKeys.EXTERNAL_MAIL.SORT_FIELDS.DATE },
  { value: 'subject', labelKey: i18nKeys.EXTERNAL_MAIL.SORT_FIELDS.SUBJECT },
  { value: 'from_mail', labelKey: i18nKeys.EXTERNAL_MAIL.SORT_FIELDS.FROM_MAIL },
] as const;

type MailToolbarProps = {
  table: Table<LightThread>;
  onClearSelection: () => void;
  isSelectAllPages: boolean;
  form: MailFiltersForm;
  showCategories: boolean;
  totalCount: number;
};

export const MailToolbar = ({
  table,
  onClearSelection,
  isSelectAllPages,
  form,
  showCategories,
  totalCount,
}: MailToolbarProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const company = useCompanyConfiguration();

  const { batchUpdateExternalMails } = useBatchUpdateExternalMails();
  const { manuallySyncExternalEmails, isManuallySyncExternalEmailsLoading } =
    useManuallySyncExternalMails();

  const [isMarkedRead, setIsMarkedRead] = useState(false);
  const [isMarkedProcessed, setIsMarkedProcessed] = useState(false);

  const { sortBy, sortOrder } = form.values;
  const selectedIds = isSelectAllPages ? [] : Object.keys(table.getState().rowSelection);
  const hasSelection = isSelectAllPages || Object.keys(table.getState().rowSelection).length > 0;

  useEffect(() => {
    setIsMarkedRead(false);
    setIsMarkedProcessed(false);
  }, [table.getState().rowSelection]);

  const reportBatchAction = (actionType: ActionTypes) => {
    captureEvent(
      posthog,
      'views:batch_action_executed',
      buildBatchActionProperties({
        actionType,
        isSelectedAll: isSelectAllPages,
        selectedCount: selectedIds.length,
        totalObjects: totalCount,
        page: pathname,
      }),
    );
  };

  const handleBatchReadToggle = () => {
    const actionType = isMarkedRead ? ActionTypes.unread : ActionTypes.read;

    reportBatchAction(actionType);
    batchUpdateExternalMails({ actionType, threadIds: selectedIds });
    setIsMarkedRead(!isMarkedRead);
  };

  const handleBatchProcessToggle = () => {
    const actionType = isMarkedProcessed ? ActionTypes.unprocess : ActionTypes.process;

    reportBatchAction(actionType);
    batchUpdateExternalMails({ actionType, threadIds: selectedIds });
    setIsMarkedProcessed(!isMarkedProcessed);
  };

  const handleBatchDelete = () => {
    modals.openConfirmModal({
      title: t(tKeys.CONFIRM),
      centered: true,
      children: <Text>{t(tKeys.AYS.DELETE_EXTERNAL_MAILS)}</Text>,
      labels: { confirm: t(tKeys.DELETE), cancel: t(tKeys.CANCEL) },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        reportBatchAction(ActionTypes.delete);
        batchUpdateExternalMails(
          { actionType: ActionTypes.delete, threadIds: selectedIds },
          { onSuccess: onClearSelection },
        );
      },
    });
  };

  const handleBatchAssign = () => {
    modals.open({
      title: t(i18nKeys.TASK.ACTIONS.ASSIGN),
      centered: true,
      children: (
        <BatchAssignModal
          onSubmit={({ debtorId, accountManagerId }) => {
            reportBatchAction(ActionTypes.assign);
            batchUpdateExternalMails(
              {
                actionType: ActionTypes.assign,
                threadIds: selectedIds,
                debtorId: debtorId ?? undefined,
                accountManagerId: accountManagerId ?? undefined,
              },
              { onSuccess: () => modals.closeAll() },
            );
          }}
        />
      ),
    });
  };

  const activeSortField = SORT_FIELDS.find(f => f.value === sortBy);
  const activeSortLabel = activeSortField
    ? t(activeSortField.labelKey)
    : t(i18nKeys.EXTERNAL_MAIL.SORT_FIELDS.SORT);

  return (
    <Stack gap={0}>
      <Group my="space2" ml="space4">
        <Checkbox
          size="xs"
          color="blue"
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
        {hasSelection ? (
          <>
            <Tooltip
              label={t(
                isMarkedRead
                  ? i18nKeys.EXTERNAL_MAIL.ACTIONS.UNREAD
                  : i18nKeys.EXTERNAL_MAIL.ACTIONS.READ,
              )}
              withArrow
            >
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={t(
                  isMarkedRead
                    ? i18nKeys.EXTERNAL_MAIL.ACTIONS.UNREAD
                    : i18nKeys.EXTERNAL_MAIL.ACTIONS.READ,
                )}
                onClick={handleBatchReadToggle}
              >
                {isMarkedRead ? (
                  <IconMailOpened size={18} stroke={1.5} />
                ) : (
                  <IconMail size={18} stroke={1.5} />
                )}
              </ActionIcon>
            </Tooltip>
            <Tooltip label={t(i18nKeys.EXTERNAL_MAIL.ACTIONS.ASSIGN)} withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={t(i18nKeys.EXTERNAL_MAIL.ACTIONS.ASSIGN)}
                onClick={handleBatchAssign}
              >
                <IconUser size={18} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
            <Tooltip
              label={t(
                isMarkedProcessed
                  ? i18nKeys.EXTERNAL_MAIL.ACTIONS.UNPROCESS
                  : i18nKeys.EXTERNAL_MAIL.ACTIONS.PROCESS,
              )}
              withArrow
            >
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={t(
                  isMarkedProcessed
                    ? i18nKeys.EXTERNAL_MAIL.ACTIONS.UNPROCESS
                    : i18nKeys.EXTERNAL_MAIL.ACTIONS.PROCESS,
                )}
                onClick={handleBatchProcessToggle}
              >
                {isMarkedProcessed ? (
                  <IconX size={18} stroke={1.5} />
                ) : (
                  <IconCheck size={18} stroke={1.5} />
                )}
              </ActionIcon>
            </Tooltip>
            <Tooltip label={t(i18nKeys.EXTERNAL_MAIL.ACTIONS.DELETE)} withArrow>
              <ActionIcon
                variant="subtle"
                color="red"
                aria-label={t(i18nKeys.EXTERNAL_MAIL.ACTIONS.DELETE)}
                onClick={handleBatchDelete}
              >
                <IconTrash size={18} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip
              label={t(i18nKeys.NOT_INCLUDED)}
              disabled={company.package.can_use_external_mails}
            >
              <ActionIcon
                variant="subtle"
                color="gray"
                loading={isManuallySyncExternalEmailsLoading}
                disabled={!company.package.can_use_external_mails}
                onClick={() => manuallySyncExternalEmails()}
              >
                <IconRefresh size={18} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
            {showCategories && <MailCategoryFilterBar form={form} />}
          </>
        )}
        <Group ml="auto" mr="space4" gap={4}>
          <Tooltip
            label={t(
              sortOrder === 'asc'
                ? i18nKeys.EXTERNAL_MAIL.ACTIONS.ASCENDING
                : i18nKeys.EXTERNAL_MAIL.ACTIONS.DESCENDING,
            )}
            withArrow
          >
            <ActionIcon
              variant="subtle"
              color={sortBy ? 'purple' : 'gray'}
              disabled={!sortBy}
              onClick={() => form.setFieldValue('sortOrder', sortOrder === 'asc' ? 'desc' : 'asc')}
            >
              {sortOrder === 'asc' ? (
                <IconSortAscending size={18} stroke={1.5} />
              ) : (
                <IconSortDescending size={18} stroke={1.5} />
              )}
            </ActionIcon>
          </Tooltip>
          <Menu shadow="sm">
            <Menu.Target>
              <Button
                variant="subtle"
                color={sortBy ? 'purple' : 'gray'}
                size="compact-xs"
                rightSection={<IconChevronDown size={12} stroke={1.5} />}
              >
                {activeSortLabel}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {SORT_FIELDS.map(field => (
                <Menu.Item
                  key={field.value}
                  leftSection={sortBy === field.value ? <IconCheck size={14} stroke={1.5} /> : null}
                  onClick={() =>
                    form.setFieldValue('sortBy', sortBy === field.value ? undefined : field.value)
                  }
                >
                  {t(field.labelKey)}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>
      <Divider />
    </Stack>
  );
};

const BatchAssignModal = ({
  onSubmit,
}: {
  onSubmit: (values: { debtorId: string | null; accountManagerId: string | null }) => void;
}) => {
  const { t } = useTranslation();
  const form = useExternalMailActionForm({
    initialValues: { id: '', debtorId: null, userId: null, debtor: null },
  });

  return (
    <ExternalMailActionFormProvider form={form}>
      <form
        onSubmit={form.onSubmit(values => {
          onSubmit({ debtorId: values.debtorId, accountManagerId: values.userId });
        })}
      >
        <Stack p="md" gap="sm">
          <ExternalMailActionDebtor />
          <ExternalMailActionAccountManager />
          <Group justify="end">
            <Button variant="outline" onClick={() => modals.closeAll()} mt="xs">
              {t(i18nKeys.CANCEL)}
            </Button>
            <Button disabled={!form.isDirty()} type="submit" mt="xs">
              {t(i18nKeys.SAVE)}
            </Button>
          </Group>
        </Stack>
      </form>
    </ExternalMailActionFormProvider>
  );
};
